import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentsDataIO } from '../types';

export const getGetAppointmentsDataQueryKey = (req: GetAppointmentsDataIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentsData', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentsDataQueryKey>;

/**
 * A hook that returns a query for the `GetAppointmentsData` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsDataQuery = <E = unknown, D = GetAppointmentsDataIO['output']>(
  req: GetAppointmentsDataIO['input'],
  options?: LimitedSchemaQueryOptions<GetAppointmentsDataIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetAppointmentsDataIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentsDataQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsData(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetAppointmentsData` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsDataQueries = <E = unknown, D = GetAppointmentsDataIO['output']>(
  argsArr: {
    req: GetAppointmentsDataIO['input'];
    options?: LimitedSchemaQueryOptions<GetAppointmentsDataIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetAppointmentsDataQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsData(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetAppointmentsDataQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentsData` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentsData = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentsData = async <E = unknown, D = GetAppointmentsDataIO['output']>(
    req: GetAppointmentsDataIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetAppointmentsDataIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetAppointmentsDataIO['output'], E, D, QueryKey>(
      getGetAppointmentsDataQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentsData(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentsData;
};
